import { Box, Button } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { get } from 'lodash';

type RepeaterWrapperProps = {
  name: string;
  disabled?: boolean;
  minNumberOfEntries?: number;
  maxNumberOfEntries?: number;
  renderField: (index: number) => React.ReactNode;
  onChangeCallback?: (nameList: string[]) => void;
};

export const RepeaterWrapper = ({
  name,
  disabled = false,
  minNumberOfEntries = 0,
  maxNumberOfEntries = 20,
  renderField,
  onChangeCallback,
}: RepeaterWrapperProps) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const values = useWatch({
    control,
    name,
  }) as string[] | undefined;

  const items = values ?? [];

  useEffect(() => {
    if (items.length === 0 && minNumberOfEntries > 0) {
      setValue(
        name,
        Array.from({ length: minNumberOfEntries }, () => ''),
        {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        },
      );
    }
  }, [items.length, minNumberOfEntries, name, setValue]);

  const handleAdd = () => {
    setValue(name, [...items, ''], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleRemove = (index: number) => {
    const next = items.filter((_, i) => i !== index);

    setValue(name, next, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    if (onChangeCallback) onChangeCallback([name]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((_, index) => {
        const hasError = !!get(errors, `${name}.${index}`);
        return (
          <Box
            key={`${name}-${index}-${items[index] ?? 'empty'}`}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: hasError ? 'flex-start' : 'center',
            }}
          >
            <Box sx={{ flex: 1 }}>{renderField(index)}</Box>

            <Button
              type='button'
              onClick={() => handleRemove(index)}
              disabled={disabled || items.length <= 1}
              color='error'
              variant='text'
              sx={{
                minWidth: '40px',
                ...(hasError && { pt: '20px' }),
              }}
            >
              <DeleteIcon />
            </Button>
          </Box>
        );
      })}

      <Button
        startIcon={<AddIcon sx={{ transform: 'scale(1.2)' }} />}
        sx={{
          fontSize: '1rem',
          width: 'fit-content',
          fontWeight: 'medium',
        }}
        color='primary'
        variant='text'
        onClick={handleAdd}
        disabled={disabled || items.length >= maxNumberOfEntries}
      >
        ADD
      </Button>
    </Box>
  );
};
